"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useScopedI18n } from "@/messages/client";
import { Column, ColumnOrderState, Table } from "@tanstack/react-table";
import { Columns3, GripVertical } from "lucide-react";
import { useDrag, useDrop } from "react-dnd";

const reorderColumn = (
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  );
  return [...columnOrder];
};

function Draggable<T>(props: { table: Table<T>; column: Column<T, unknown> }) {
  const { getState, setColumnOrder } = props.table;

  const { columnOrder } = getState();

  const [, dropRef] = useDrop({
    accept: "column",
    drop: (draggedColumn: Column<T>) => {
      const newColumnOrder = reorderColumn(
        draggedColumn.id,
        props.column.id,
        columnOrder
      );

      setColumnOrder(newColumnOrder);
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => props.column,
    type: "column",
  });

  return (
    <div ref={dropRef}>
      <div
        ref={previewRef}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        className="group flex h-11 items-center space-x-5 border-b border-[#D1D1D1]">
        <div ref={dragRef}>
          <GripVertical className="cursor-grab transition-colors duration-300 group-hover:text-primary" />
        </div>

        <Checkbox
          id={props.column.id}
          checked={props.column.getIsVisible()}
          onCheckedChange={(checked) => {
            props.column.toggleVisibility(checked as boolean);
          }}
        />
        <label
          htmlFor={props.column.id}
          className="cursor-pointer select-none text-[#26272A] transition-colors duration-300 hover:text-primary">
          {props.column.columnDef.header as string}
        </label>
      </div>
    </div>
  );
}

export default function ColumnSetting<T>(props: { table: Table<T> }) {
  const t = useScopedI18n("选型器");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="activeOutline" className="space-x-2 rounded-md">
          <Columns3 size={20} />

          <span>{t("列设置")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="z-50 w-[400px] rounded-sm border-none bg-white px-0 py-3 shadow-[0px_2px_9px_0px_rgba(185,185,185,0.5)]">
        <p className="px-5 text-sm text-[#26272A]">
          {t("勾选您需要的参数条件，或拖动排序")}
        </p>
        <div className="max-h-80 overflow-y-auto px-5">
          {props.table
            .getAllLeafColumns()
            .filter(
              (column) =>
                column.id !== "material_number" && column.id !== "images"
            )
            .map((column) => {
              return (
                <Draggable
                  key={column.id}
                  table={props.table}
                  column={column}
                />
              );
            })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
